import {
  ChangeEventHandler,
  FunctionComponent,
  MutableRefObject,
  useRef,
  useState,
} from "react";
import { resources } from "../../utils/resources";
import Button from "../button/Button";
import styles from "./Input.module.scss";
import eyePassword from "../../assets/icons/eye-solid.svg";
import eyePasswordSlash from "../../assets/icons/eye-slash-solid.svg";

export interface InputValidation {
  maxLength?: number;
  transform?: (inputValue: string) => string;
  required?: boolean;
  /**
   *
   * @returns error message, if any, as string
   */
  onChangeValidation?: (inputValue: string) => string;
}

interface InputProps {
  icon?: string;
  startSymbol?: string | JSX.Element;
  onChange?: (value: string) => void;
  value: string | number;
  placeholder?: string;
  name?: string;
  dimmed?: boolean;
  endSymbol?: string | JSX.Element;
  responsive?: boolean;
  number?: boolean;
  type?: "email" | "password" | "text" | "search" | "number" | "date" | "time";
  required?: boolean;
  className?: string;
  onInputClear?: () => void;
  disabled?: boolean;
  noBorder?: boolean;
  showPasswordIcon?: boolean;
  refValue?: any;
  onBlur?: () => void;
  inputValidation?: InputValidation;
  id?: string;
  autoComplete?: "phone" | "email" | "new-password" | "off";
  step?: string;
  style?: React.CSSProperties;
}

const Input: FunctionComponent<InputProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errMessage, setErrMessage] = useState("");

  const {
    icon,
    startSymbol,
    onChange,
    value,
    placeholder,
    name,
    dimmed,
    endSymbol: _endSymbol,
    responsive,
    number,
    type,
    step,
    required,
    className,
    onInputClear,
    disabled,
    noBorder,
    refValue: _refValue,
    onBlur,
    inputValidation,
    id,
    showPasswordIcon,
    autoComplete,
    style,
  } = props;

  const internalRef = useRef(null);
  const refValue: MutableRefObject<HTMLInputElement | null> =
    _refValue || internalRef;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    const input = refValue.current;
    if (input) {
      setTimeout(() => {
        input.setSelectionRange(input.value.length, input.value.length);
        input.focus();
      });
    }
  };

  const passwordIcon =
    showPasswordIcon && type === "password" ? (
      <Button
        iconOnly
        type="transparent"
        tooltip={showPassword ? "Hide password" : "Show password"}
        onClick={togglePasswordVisibility}
      >
        <img
          alt={showPassword ? "Hide password" : "Show password"}
          src={showPassword ? eyePasswordSlash : eyePassword}
          className="generic-icon"
        />
      </Button>
    ) : (
      ""
    );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (onChange) {
      let output = e.target.value;
      if (number) {
        output = Number(output.replace(/\D/g, "")).toLocaleString();
        output = output === "0" ? "" : output;
      }
      if (inputValidation) {
        if (inputValidation.transform) {
          output = inputValidation.transform(output);
        }
        if (inputValidation.maxLength) {
          output = output.slice(0, inputValidation.maxLength);
        }
        const errMessage = inputValidation.onChangeValidation?.(output);
        setErrMessage(errMessage || "");
      }
      onChange(output);
    }
  };

  const endSymbol = _endSymbol || passwordIcon;

  const displayValue =
    number && typeof value === "number" ? value.toLocaleString() : value;

  const activePasswordType = showPassword ? "text" : "password";

  return (
    <div
      className={[
        styles.wrapper,
        icon ? styles["with-icon"] : "",
        noBorder && styles["no-border"],
        responsive && styles.responsive,
        className,
      ].join(" ")}
    >
      {icon && <img alt="input icon" src={icon} className={styles.icon} />}
      {startSymbol && (
        <span className={[styles.symbol, styles.start].join(" ")}>
          <span>{startSymbol}</span>
        </span>
      )}
      <input
        className={[
          styles.input,
          icon ? styles["with-icon"] : "",
          dimmed ? styles.dimmed : "",
          startSymbol && styles["padding-left"],
          onInputClear && styles["padding-right"],
        ].join(" ")}
        onChange={handleChange}
        value={displayValue || ""}
        name={name}
        placeholder={placeholder}
        type={type === "password" ? activePasswordType : type || "text"}
        required={required || inputValidation?.required}
        disabled={disabled}
        ref={refValue}
        autoComplete={autoComplete}
        onBlur={onBlur}
        id={id}
      />
      {endSymbol && (
        <span className={[styles.symbol, styles.end].join(" ")}>
          <span>{endSymbol}</span>
        </span>
      )}
      {onInputClear && Boolean(value) && (
        <span className={[styles.symbol, styles.end].join(" ")}>
          <Button type="transparent" iconOnly>
            <img
              alt="clear"
              onClick={onInputClear}
              src={resources.icons.CLEAR}
              className={styles["clear-icon"]}
            />
          </Button>
        </span>
      )}
      <span
        className={[styles["error-message"], errMessage && styles.active].join(
          " "
        )}
      >
        {errMessage}
      </span>
    </div>
  );
};

interface TextAreaProps {
  icon?: string;
  onChange: (value: string) => void;
  value: string | number;
  placeholder?: string;
  name?: string;
  dimmed?: boolean;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  refValue?: any;
  onBlur?: () => void;
  inputValidation?: InputValidation;
  id?: string;
  hideResize?: boolean;
  responsive?: boolean;
}

export const TextArea: FunctionComponent<TextAreaProps> = (props) => {
  const {
    icon,
    onChange,
    value,
    placeholder,
    name,
    dimmed,
    required,
    className,
    disabled,
    refValue,
    onBlur,
    inputValidation,
    id,
    hideResize,
    responsive,
  } = props;

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    let output = e.target.value;
    if (inputValidation) {
      if (inputValidation.transform) {
        output = inputValidation.transform(output);
      }
      if (inputValidation.maxLength) {
        output = output.slice(0, inputValidation.maxLength);
      }
    }
    onChange(output);
  };

  return (
    <div
      className={[
        styles.wrapper,
        icon ? styles["with-icon"] : "",
        responsive && styles.responsive,
      ].join(" ")}
    >
      {icon && <img alt="input icon" src={icon} className={styles.icon} />}
      <textarea
        className={[
          styles.input,
          icon ? styles["with-icon"] : "",
          dimmed ? styles.dimmed : "",
          hideResize && styles["hide-resize"],
          className,
        ].join(" ")}
        rows={6}
        onChange={handleChange}
        value={value || ""}
        name={name}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        ref={refValue}
        onBlur={onBlur}
        id={id}
      />
    </div>
  );
};

export default Input;
